import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, Grid, CircularProgress,
  Paper, Divider, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, useTheme, useMediaQuery, Card, CardContent,
  InputAdornment, IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import HotelIcon from "@mui/icons-material/Hotel";
import ClearIcon from "@mui/icons-material/Clear";
import dayjs from "dayjs";
import API from "../API/API";
import { getImageUrl } from "../utils/imageHelper";
import { toast } from "react-toastify";

const api = new API();

const tableHeaderStyle = {
  bgcolor: "#0b1120",
  color: "#00d7d7",
  fontSize: "11px",
  fontWeight: "bold",
  border: "1px solid #1c2b4d",
  whiteSpace: "nowrap",
  py: 1,
  px: 2,
};

const cellStyle = {
  color: "#e0e6f0",
  fontSize: "11px",
  border: "1px solid #1c2b4d",
  whiteSpace: "nowrap",
  py: 0.8,
  px: 1.5,
};

export default function CustomerVisits() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.warning("Please enter a Name or Phone Number to search");
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      // Try phone number search first, then fallback to name search
      const isPhone = /^\d+$/.test(query.trim());
      const url = isPhone
        ? `api/guest-images?phone_number=${query.trim()}`
        : `api/guest-images?guest_name=${query.trim()}`;
      const res = await api.get(url);
      setResults(res?.data?.data || res?.data || []);
    } catch (e) {
      toast.error("Failed to fetch customer records");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setSearched(false);
  };

  // Auto-search helper — called when 10-digit phone is detected
  const handleAutoSearch = async (phoneVal) => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await api.get(`api/guest-images?phone_number=${phoneVal}`);
      setResults(res?.data?.data || res?.data || []);
    } catch (e) {
      toast.error("Failed to fetch customer records");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Group results by guest (phone/name)
  const grouped = results.reduce((acc, item) => {
    const key = item.phone_number || item.guest_name || "unknown";
    if (!acc[key]) acc[key] = { info: item, visits: [] };
    acc[key].visits.push(item);
    return acc;
  }, {});

  const groups = Object.values(grouped);

  return (
    <Box sx={{ bgcolor: "#0b1120", minHeight: "100vh", p: isMobile ? 1 : 2 }}>
      {/* Page Header */}
      <Box
        sx={{
          bgcolor: "#111c31",
          borderRadius: "10px",
          border: "1px solid #1c2b4d",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            bgcolor: "#0d1b3e",
            py: 1.5,
            px: 2,
            borderBottom: "2px solid #00d7d7",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: "#ffd700",
              fontWeight: "bold",
              fontSize: isMobile ? "16px" : "20px",
              letterSpacing: "2px",
              textTransform: "uppercase",
            }}
          >
            🏨 Customer Visiting History
          </Typography>
          <Typography sx={{ color: "#8faac8", fontSize: "11px", mt: 0.5 }}>
            Search by Guest Name or Phone Number to view complete visit records
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box sx={{ p: 2, display: "flex", gap: 1.5, alignItems: "center", flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <Typography sx={{ color: "#8faac8", fontSize: "12px", fontWeight: "bold", whiteSpace: "nowrap" }}>
            Name / Phone No:
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter guest name or phone number..."
            value={query}
            onChange={(e) => {
              const val = e.target.value;
              setQuery(val);
              // Auto-search when exactly 10-digit phone number is entered
              const isPhone = /^\d{10}$/.test(val.trim());
              if (isPhone) {
                // slight delay so state updates first
                setTimeout(() => {
                  handleAutoSearch(val.trim());
                }, 100);
              }
            }}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#8faac8", fontSize: "18px" }} />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClear} sx={{ color: "#8faac8" }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              bgcolor: "#1a2035",
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                fontSize: "13px",
                height: "38px",
                "& fieldset": { borderColor: "#3a4a6b" },
                "&:hover fieldset": { borderColor: "#00d7d7" },
                "&.Mui-focused fieldset": { borderColor: "#00d7d7" },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
            sx={{
              bgcolor: "#008080",
              color: "#fff",
              fontWeight: "bold",
              px: 3,
              height: "38px",
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: "#006666" },
            }}
          >
            {loading ? "Searching..." : "SEARCH"}
          </Button>
        </Box>
      </Box>

      {/* Results */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress sx={{ color: "#00d7d7" }} />
            <Typography sx={{ color: "#8faac8", mt: 2 }}>Searching customer records...</Typography>
          </Box>
        </Box>
      ) : searched && results.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography sx={{ color: "#8faac8", fontSize: "16px" }}>😔 No records found for "{query}"</Typography>
          <Typography sx={{ color: "#4a5a7a", fontSize: "12px", mt: 1 }}>Try searching with a different name or phone number</Typography>
        </Box>
      ) : groups.length > 0 ? (
        groups.map((group, gIdx) => {
          const guest = group.info;
          const visits = group.visits;
          return (
            <Box key={gIdx} sx={{ mb: 3 }}>
              {/* Guest Profile Card */}
              <Paper
                elevation={4}
                sx={{
                  bgcolor: "#0e1a30",
                  border: "1px solid #00d7d744",
                  borderRadius: "10px",
                  mb: 1.5,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ bgcolor: "#0d2040", px: 2, py: 1, display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid #1c2b4d" }}>
                  <PersonIcon sx={{ color: "#00d7d7" }} />
                  <Typography sx={{ color: "#00d7d7", fontWeight: "bold", fontSize: "15px" }}>
                    {guest.guest_name || "N/A"}
                  </Typography>
                  <Chip
                    label={`${visits.length} Visit${visits.length > 1 ? "s" : ""}`}
                    size="small"
                    sx={{ bgcolor: "#4B90FC22", color: "#4B90FC", border: "1px solid #4B90FC44", fontSize: "10px", ml: "auto" }}
                  />
                </Box>

                {/* Guest Info Row */}
                <Box sx={{ px: 2, py: 1.5, display: "flex", flexWrap: "wrap", gap: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PhoneIcon sx={{ color: "#8faac8", fontSize: "16px" }} />
                    <Typography sx={{ color: "#fff", fontSize: "13px" }}>{guest.phone_number || "N/A"}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <HotelIcon sx={{ color: "#8faac8", fontSize: "16px" }} />
                    <Typography sx={{ color: "#8faac8", fontSize: "13px" }}>
                      Last Room: <span style={{ color: "#ffd700", fontWeight: "bold" }}>{guest.room_number || "N/A"}</span>
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Visit Records */}
              {isMobile ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {visits.map((visit, vIdx) => (
                    <Card key={vIdx} sx={{ bgcolor: "#111c31", border: "1px solid #1c2b4d", color: "#fff" }}>
                      <CardContent sx={{ p: 1.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography sx={{ color: "#ffd700", fontWeight: "bold" }}>Room: {visit.room_number || "N/A"}</Typography>
                          <Typography sx={{ color: "#8faac8", fontSize: "11px" }}>
                            {visit.created_at ? dayjs(visit.created_at).format("DD-MM-YYYY") : "N/A"}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "12px", color: "#8faac8" }}>Status: <span style={{ color: "#00ff00" }}>{visit.status || "N/A"}</span></Typography>
                        {/* Images */}
                        {visit.images && visit.images.length > 0 && (
                          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                            {visit.images.map((img, imgIdx) => (
                              <Box
                                key={imgIdx}
                                component="img"
                                src={getImageUrl(img)}
                                alt={`visit-${imgIdx}`}
                                sx={{ width: 60, height: 60, objectFit: "cover", borderRadius: 1, border: "1px solid #3a4a6b", cursor: "pointer" }}
                                onClick={() => window.open(getImageUrl(img), "_blank")}
                              />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{ border: "1px solid #1c2b4d", borderRadius: "8px", bgcolor: "#111c31" }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeaderStyle}>#</TableCell>
                        <TableCell sx={tableHeaderStyle}>Room No</TableCell>
                        <TableCell sx={tableHeaderStyle}>Visit Date</TableCell>
                        <TableCell sx={tableHeaderStyle}>Status</TableCell>
                        <TableCell sx={tableHeaderStyle}>Images</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visits.map((visit, vIdx) => (
                        <TableRow
                          key={vIdx}
                          hover
                          sx={{ "&:hover": { bgcolor: "rgba(0, 215, 215, 0.05)" } }}
                        >
                          <TableCell sx={{ ...cellStyle, color: "#8faac8" }}>{vIdx + 1}</TableCell>
                          <TableCell sx={{ ...cellStyle, color: "#ffd700", fontWeight: "bold" }}>{visit.room_number || "N/A"}</TableCell>
                          <TableCell sx={cellStyle}>
                            {visit.created_at ? dayjs(visit.created_at).format("DD-MM-YYYY hh:mm A") : "N/A"}
                          </TableCell>
                          <TableCell sx={cellStyle}>
                            <Chip
                              label={visit.status || "N/A"}
                              size="small"
                              sx={{
                                bgcolor: visit.status === "waiting" ? "#7c3a0044" : "#00800033",
                                color: visit.status === "waiting" ? "#ff8800" : "#00ff00",
                                fontSize: "10px",
                                height: "20px",
                                border: "1px solid currentColor",
                              }}
                            />
                          </TableCell>
                          <TableCell sx={cellStyle}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              {visit.images && visit.images.length > 0 ? (
                                visit.images.map((img, imgIdx) => (
                                  <Box
                                    key={imgIdx}
                                    component="img"
                                    src={getImageUrl(img)}
                                    alt={`img-${imgIdx}`}
                                    sx={{
                                      width: 45,
                                      height: 45,
                                      objectFit: "cover",
                                      borderRadius: 1,
                                      border: "1px solid #3a4a6b",
                                      cursor: "pointer",
                                      "&:hover": { border: "1px solid #00d7d7", transform: "scale(1.1)", transition: "all 0.2s" },
                                    }}
                                    onClick={() => window.open(getImageUrl(img), "_blank")}
                                  />
                                ))
                              ) : (
                                <Typography sx={{ color: "#4a5a7a", fontSize: "11px" }}>No images</Typography>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          );
        })
      ) : !searched ? (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <SearchIcon sx={{ color: "#1c2b4d", fontSize: "64px", mb: 2 }} />
          <Typography sx={{ color: "#4a5a7a", fontSize: "16px" }}>
            Enter a guest name or phone number above to view their complete visit history
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}
